import React, { useState, useRef } from "react";
import { PaperclipIcon, ImageIcon, SendIcon } from "lucide-react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { useAuth } from "../../context/AuthContext";
import {
  uploadPostMedia,
  uploadPostFile,
  createPost,
} from "../../lib/supabase";
import toast from "react-hot-toast";

interface PostComposerProps {
  onPostCreated?: () => void;
}

export const PostComposer: React.FC<PostComposerProps> = ({
  onPostCreated,
}) => {
  const { user } = useAuth();
  const [content, setContent] = useState("");
  const [isPosting, setIsPosting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedMedia, setSelectedMedia] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mediaInputRef = useRef<HTMLInputElement>(null);

  // Get avatar URL with fallback
  const getAvatarUrl = () => {
    if (user?.profile?.avatar_url) {
      return user.profile.avatar_url;
    }
    return "/avatars/ava1.png";
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-powerpoint",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    ];

    if (!allowedTypes.includes(file.type)) {
      toast.error("Format file tidak didukung. Gunakan PDF, DOC, atau PPT.");
      return;
    }

    // Validate file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error("Ukuran file terlalu besar. Maksimal 10MB.");
      return;
    }

    setSelectedFile(file);
    setSelectedMedia(null); // Clear media if file is selected

    // Clear media input
    if (mediaInputRef.current) {
      mediaInputRef.current.value = "";
    }
  };

  const handleMediaSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "video/mp4"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Format media tidak didukung. Gunakan JPG, PNG, atau MP4.");
      return;
    }

    // Validate file size (50MB limit)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      toast.error("Ukuran media terlalu besar. Maksimal 50MB.");
      return;
    }

    setSelectedMedia(file);
    setSelectedFile(null); // Clear file if media is selected

    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handlePost = async () => {
    if (!user?.id) return;
    if (!content.trim() && !selectedFile && !selectedMedia) {
      toast.error("Tulis sesuatu atau pilih file untuk dibagikan");
      return;
    }

    setIsPosting(true);

    try {
      let mediaUrl: string | undefined;
      let mediaType: string | undefined;
      let fileUrl: string | undefined;
      let fileName: string | undefined;
      let fileType: string | undefined;
      let fileSize: number | undefined;

      // Upload media if selected
      if (selectedMedia) {
        const mediaResult = await uploadPostMedia(user.id, selectedMedia);
        if (!mediaResult.success) {
          toast.error(mediaResult.error || "Gagal mengunggah media");
          return;
        }
        mediaUrl = mediaResult.url;
        mediaType = selectedMedia.type.startsWith("image/") ? "image" : "video";
      }

      // Upload file if selected
      if (selectedFile) {
        const fileResult = await uploadPostFile(user.id, selectedFile);
        if (!fileResult.success) {
          toast.error(fileResult.error || "Gagal mengunggah file");
          return;
        }
        fileUrl = fileResult.url;
        fileName = selectedFile.name;
        fileType = selectedFile.type;
        fileSize = selectedFile.size;
      }

      // Create post
      const postResult = await createPost(user.id, {
        content: content.trim(),
        media_url: mediaUrl,
        media_type: mediaType,
        file_url: fileUrl,
        file_name: fileName,
        file_type: fileType,
        file_size: fileSize,
      });

      if (postResult.success) {
        toast.success("Post berhasil dibagikan!");

        // Reset form
        setContent("");
        setSelectedFile(null);
        setSelectedMedia(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        if (mediaInputRef.current) mediaInputRef.current.value = "";

        // Notify parent component
        if (onPostCreated) {
          onPostCreated();
        }
      } else {
        toast.error(postResult.error || "Gagal membuat post");
      }
    } catch (error) {
      console.error("Error creating post:", error);
      toast.error("Terjadi kesalahan saat membuat post");
    } finally {
      setIsPosting(false);
    }
  };

  const removeSelectedFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeSelectedMedia = () => {
    setSelectedMedia(null);
    if (mediaInputRef.current) mediaInputRef.current.value = "";
  };

  return (
    <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200 shadow-sm">
      <div className="flex items-start gap-3 sm:gap-4">
        {/* User Avatar */}
        <Avatar className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0">
          <AvatarFallback>
            <img
              className="w-full h-full object-cover"
              alt="User Avatar"
              src={getAvatarUrl()}
            />
          </AvatarFallback>
        </Avatar>

        {/* Post Content */}
        <div className="flex-1 space-y-3 sm:space-y-4 min-w-0">
          {/* Text Input */}
          <Textarea
            value={content}
            onChange={e => setContent(e.target.value)}
            placeholder="Share your teaching resources or ideas..."
            className="min-h-[80px] sm:min-h-[100px] rounded-xl border-2 border-[#e3e6eb] focus:border-[#6b2cfe] transition-colors text-sm resize-none bg-white"
            disabled={isPosting}
          />

          {/* Selected File Preview */}
          {selectedFile && (
            <div className="bg-gray-50 rounded-lg p-3 flex items-center gap-2 max-w-full">
              <PaperclipIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <span
                className="text-sm text-gray-700 flex-1 min-w-0 truncate"
                title={selectedFile.name}
              >
                {selectedFile.name}
              </span>
              <span className="text-xs text-gray-500 flex-shrink-0">
                ({(selectedFile.size / 1024 / 1024).toFixed(1)} MB)
              </span>
              <button
                onClick={removeSelectedFile}
                className="text-red-500 hover:text-red-700 text-sm flex-shrink-0"
                disabled={isPosting}
              >
                Remove
              </button>
            </div>
          )}

          {/* Selected Media Preview */}
          {selectedMedia && (
            <div className="bg-gray-50 rounded-lg p-3 max-w-full">
              <div className="flex items-center gap-2 mb-2">
                <ImageIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                <span
                  className="text-sm text-gray-700 flex-1 min-w-0 truncate"
                  title={selectedMedia.name}
                >
                  {selectedMedia.name}
                </span>
                <span className="text-xs text-gray-500 flex-shrink-0">
                  ({(selectedMedia.size / 1024 / 1024).toFixed(1)} MB)
                </span>
                <button
                  onClick={removeSelectedMedia}
                  className="text-red-500 hover:text-red-700 text-sm flex-shrink-0"
                  disabled={isPosting}
                >
                  Remove
                </button>
              </div>
              {/* Media Preview */}
              {selectedMedia.type.startsWith("image/") && (
                <img
                  src={URL.createObjectURL(selectedMedia)}
                  alt="Preview"
                  className="w-full max-w-xs h-32 object-cover rounded-lg"
                />
              )}
              {selectedMedia.type.startsWith("video/") && (
                <video
                  src={URL.createObjectURL(selectedMedia)}
                  className="w-full max-w-xs h-32 object-cover rounded-lg"
                  controls
                />
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {/* Hidden file inputs */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.ppt,.pptx"
                onChange={handleFileSelect}
                className="hidden"
                disabled={isPosting}
              />
              <input
                ref={mediaInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,video/mp4"
                onChange={handleMediaSelect}
                className="hidden"
                disabled={isPosting}
              />

              {/* File Upload Button - Now always enabled (except when posting) */}
              <Button
                onClick={() => fileInputRef.current?.click()}
                variant="outline"
                size="sm"
                className="flex items-center gap-2 text-[#373e46] border-gray-300 hover:bg-gray-50 h-8 sm:h-10 px-2 sm:px-3 text-xs sm:text-sm"
                disabled={isPosting}
              >
                <PaperclipIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">File</span>
              </Button>

              {/* Media Upload Button - Now always enabled (except when posting) */}
              <Button
                onClick={() => mediaInputRef.current?.click()}
                variant="outline"
                size="sm"
                className="flex items-center gap-2 text-[#373e46] border-gray-300 hover:bg-gray-50 h-8 sm:h-10 px-2 sm:px-3 text-xs sm:text-sm"
                disabled={isPosting}
              >
                <ImageIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Photo/Video</span>
              </Button>
            </div>

            {/* Post Button */}
            <Button
              onClick={handlePost}
              disabled={
                isPosting ||
                (!content.trim() && !selectedFile && !selectedMedia)
              }
              className={`px-4 sm:px-6 py-2 rounded-xl font-medium transition-all duration-300 flex items-center gap-2 h-8 sm:h-10 text-xs sm:text-sm ${
                (!content.trim() && !selectedFile && !selectedMedia) ||
                isPosting
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] hover:opacity-90 text-white hover:shadow-lg"
              }`}
            >
              {isPosting ? (
                <>
                  <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-white"></div>
                  <span>Posting...</span>
                </>
              ) : (
                <>
                  <SendIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span>Post</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
